import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import portalApi from "@/lib/portalApi";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MagnifyingGlass, ShoppingCart, Trash, Storefront, Plus, Minus, SignOut, Lock, X as XIcon } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PhotoLightbox from "@/components/PhotoLightbox";
import { passwordPolicyError, PASSWORD_HINT } from "@/lib/password";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCur = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${fmt(n)}`;

// ============ LOGIN ============
export function PortalLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("portal_token")) nav("/portal");
  }, [nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await portalApi.post("/portal/auth/login", { email, password });
      localStorage.setItem("portal_token", data.token);
      localStorage.setItem("portal_client", JSON.stringify(data.client));
      nav("/portal");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white p-8 rounded-sm relative">
        <button
          type="button"
          onClick={() => nav("/login")}
          className="absolute top-3 right-3 p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded transition"
          aria-label="Cerrar y volver al login principal"
          data-testid="portal-login-close"
        >
          <XIcon size={18} weight="bold" />
        </button>
        <div className="mb-6">
          <div className="font-mono-data text-xs tracking-widest text-[#002FA7] mb-1">PORTAL DE REVENDEDORES</div>
          <h1 className="font-heading text-3xl font-extrabold">Alendra PC</h1>
          <p className="text-zinc-500 text-sm mt-1">Accede a tu catálogo personalizado.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">Correo</Label>
            <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="portal-login-email" className="mt-1" autoComplete="email" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Contraseña</Label>
            <PasswordInput value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="portal-login-password" className="mt-1" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="portal-login-submit">
            {loading ? "Entrando…" : "Entrar al portal"}
          </Button>
        </form>
        <p className="text-[11px] text-zinc-400 mt-5 text-center">
          ¿No tienes acceso? Contacta al equipo Alendra PC.
        </p>
      </div>
    </div>
  );
}

// ============ MAIN PORTAL (catalog + cart) ============
export function PortalApp() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sort, setSort] = useState("default");  // default | price_asc | price_desc
  const [cart, setCart] = useState({});  // {product_id: qty}
  const [showCart, setShowCart] = useState(false);
  const [showOrders, setShowOrders] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [showWithDiscount, setShowWithDiscount] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [pwd, setPwd] = useState({ cur: "", next: "", confirm: "" });
  const [lb, setLb] = useState({ open: false, photos: [], title: "", initialIndex: 0 });

  const load = useCallback(async () => {
    try {
      const params = { q, category: cat };
      if (sort !== "default") params.sort = sort;
      const { data } = await portalApi.get("/portal/products", { params });
      setProducts(data.products);
      setCategories(data.categories);
    } catch (err) { console.warn("Portal products load failed:", err); }
  }, [q, cat, sort]);

  useEffect(() => {
    portalApi.get("/portal/me").then((r) => {
      setMe(r.data);
      if (r.data.must_change_password) setShowChangePwd(true);
    }).catch(() => nav("/portal/login"));
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const cartCount = useMemo(() => Object.values(cart).reduce((a, b) => a + b, 0), [cart]);
  const cartItems = useMemo(() => Object.entries(cart).map(([pid, qty]) => {
    const p = products.find(x => x.id === pid);
    if (!p) return null;
    return { ...p, qty };
  }).filter(Boolean), [cart, products]);
  const cartTotal = cartItems.reduce((sum, c) => sum + c.portal_price * c.qty, 0);
  const cartCurrency = cartItems[0]?.currency || "MXN";

  const addToCart = (pid) => setCart((c) => ({ ...c, [pid]: (c[pid] || 0) + 1 }));
  const setQty = (pid, qty) => setCart((c) => {
    const next = { ...c };
    if (qty <= 0) delete next[pid]; else next[pid] = qty;
    return next;
  });

  const submitOrder = async () => {
    if (cartItems.length === 0) { toast.error("El carrito está vacío"); return; }
    setSubmitting(true);
    try {
      await portalApi.post("/portal/orders", {
        items: cartItems.map(c => ({ product_id: c.id, qty: c.qty })),
        notes: "",
      });
      toast.success("¡Solicitud enviada! El equipo Alendra PC se pondrá en contacto contigo pronto.");
      setCart({});
      setShowCart(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar la solicitud");
    } finally { setSubmitting(false); }
  };

  const openOrders = async () => {
    try {
      const { data } = await portalApi.get("/portal/orders");
      setOrders(data);
      setShowOrders(true);
    } catch (err) { toast.error("Error"); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.next !== pwd.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    const policyErr = passwordPolicyError(pwd.next);
    if (policyErr) { toast.error(policyErr); return; }
    try {
      await portalApi.post("/portal/auth/change-password", { current_password: pwd.cur, new_password: pwd.next });
      toast.success("Contraseña actualizada");
      setShowChangePwd(false);
      setPwd({ cur: "", next: "", confirm: "" });
      setMe((m) => ({ ...m, must_change_password: false }));
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const logout = () => {
    localStorage.removeItem("portal_token");
    localStorage.removeItem("portal_client");
    nav("/portal/login");
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Cargando…</div>;
  const discount = me.discount_pct || 0;
  const tierLabel = me.client_type === "reseller" ? "Reventa" : "Distribuidor";

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono-data text-[10px] tracking-widest text-[#002FA7]">PORTAL DE REVENDEDORES</div>
            <div className="font-heading text-lg font-bold inline-flex items-center gap-2"><Storefront size={18}/> Alendra PC</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[10px] font-mono-data px-2 py-1 bg-[#002FA7]/10 text-[#002FA7] rounded uppercase tracking-wider" data-testid="portal-tier-badge">
              Precio {tierLabel}
            </span>
            {discount > 0 && (
              <span className="text-xs font-mono-data px-2 py-1 bg-[#00873D]/15 text-[#00873D] rounded">
                Tienes <b>{discount}%</b> de descuento
              </span>
            )}
            <span className="text-xs text-zinc-500 hidden md:inline">{me.display_name}</span>
            <Button variant="outline" size="sm" onClick={openOrders} data-testid="my-orders-btn" className="gap-1">
              Mis solicitudes
            </Button>
            <Button variant="outline" size="sm" onClick={()=>setShowChangePwd(true)} className="gap-1" title="Cambiar contraseña">
              <Lock size={14}/>
            </Button>
            <Button onClick={()=>setShowCart(true)} data-testid="open-cart-btn" className="bg-[#002FA7] hover:bg-[#002277] text-white gap-1.5 relative">
              <ShoppingCart size={16} weight="bold" /> Carrito
              {cartCount > 0 && (
                <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] text-[10px] font-bold bg-white text-[#002FA7] rounded-full px-1">{cartCount}</span>
              )}
            </Button>
            <button onClick={logout} className="text-zinc-400 hover:text-[#E0000F] p-2" title="Cerrar sesión">
              <SignOut size={16}/>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Buscar producto, marca, SKU…" className="pl-9" data-testid="portal-search" />
          </div>
          <select value={cat} onChange={(e)=>setCat(e.target.value)} className="border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white" data-testid="portal-cat">
            <option value="all">Todas las categorías</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={sort} onChange={(e)=>setSort(e.target.value)} className="border border-zinc-300 rounded-sm px-3 py-2 text-sm bg-white" data-testid="portal-sort">
            <option value="default">Orden por defecto</option>
            <option value="price_asc">Precio: menor a mayor</option>
            <option value="price_desc">Precio: mayor a menor</option>
          </select>
          {discount > 0 && (
            <label className="text-xs text-zinc-600 inline-flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={showWithDiscount} onChange={(e)=>setShowWithDiscount(e.target.checked)} data-testid="toggle-discount" />
              Mostrar mi precio con descuento
            </label>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="portal-grid">
          {products.length === 0 && (
            <div className="col-span-full text-center py-16 text-zinc-400 text-sm">Sin productos disponibles</div>
          )}
          {products.map((p) => {
            const allPhotos = [
              ...(Array.isArray(p.photos) ? p.photos.filter(Boolean) : []),
            ];
            if (allPhotos.length === 0 && p.photos_url) allPhotos.push(p.photos_url);
            const photo = allPhotos[0] || "";
            const extra = Math.max(0, allPhotos.length - 1);
            return (
              <div key={p.id} className="bg-white border border-zinc-200 hover:border-[#002FA7] hover:shadow-sm transition flex flex-col" data-testid={`product-${p.id}`}>
                <button
                  type="button"
                  onClick={() => allPhotos.length > 0 && setLb({ open: true, photos: allPhotos, title: p.name, initialIndex: 0 })}
                  className="aspect-square bg-zinc-50 border-b border-zinc-100 flex items-center justify-center overflow-hidden relative group cursor-zoom-in"
                  data-testid={`product-thumb-${p.id}`}
                  aria-label={`Ver fotos de ${p.name}`}
                >
                  {photo
                    ? <img src={photo} alt={p.name} className="max-w-full max-h-full object-contain transition-transform group-hover:scale-[1.03]" loading="lazy" />
                    : <span className="text-[10px] text-zinc-300">sin foto</span>}
                  {extra > 0 && (
                    <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 text-[10px] font-mono-data bg-black/70 text-white rounded">+{extra}</span>
                  )}
                </button>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="text-[10px] font-mono-data uppercase text-zinc-400">{p.category}</div>
                  <div className="font-semibold text-sm mt-1 line-clamp-2">{p.name}</div>
                  <div className="text-[11px] text-zinc-500">{p.brand || ""}</div>
                  {p.characteristics && <div className="text-[10px] text-zinc-400 mt-1 line-clamp-2">{p.characteristics}</div>}
                  <div className="mt-3 flex-1"/>
                  <div className="flex items-end justify-between">
                    <div>
                      {showWithDiscount && discount > 0 ? (
                        <>
                          <div className="text-[10px] text-zinc-400 line-through numeric">{fmtCur(p.marketplace_price, p.currency)}</div>
                          <div className="numeric text-lg font-bold text-[#00873D]">{fmtCur(p.portal_price, p.currency)}</div>
                        </>
                      ) : (
                        <div className="numeric text-lg font-bold">{fmtCur(p.marketplace_price, p.currency)}</div>
                      )}
                      {p.suggested_price > 0 && (
                        <div className="mt-1.5 text-[10px] leading-tight">
                          <span className="font-mono-data uppercase tracking-wider text-[#002FA7]/70">Precio sugerido al cliente:</span>
                          <span className="numeric text-zinc-700 font-semibold ml-1">{fmtCur(p.suggested_price, p.currency)}</span>
                        </div>
                      )}
                      <div className="text-[10px] text-zinc-500 numeric mt-0.5">Stock: {p.stock}</div>
                    </div>
                    <Button size="sm" onClick={()=>addToCart(p.id)} className="bg-[#002FA7] hover:bg-[#002277] text-white gap-1" data-testid={`add-${p.id}`}>
                      <Plus size={12} weight="bold" /> Agregar
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* CART */}
      <Dialog open={showCart} onOpenChange={setShowCart}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cart-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Mi carrito</DialogTitle>
          </DialogHeader>
          {cartItems.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 dotted-bg">El carrito está vacío.</div>
          ) : (
            <div className="space-y-2">
              {cartItems.map(it => (
                <div key={it.id} className="flex items-center gap-3 border border-zinc-200 p-3 rounded-sm">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{it.name}</div>
                    <div className="text-[11px] text-zinc-500">{it.brand} · {fmtCur(it.portal_price, it.currency)} c/u</div>
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <button onClick={()=>setQty(it.id, it.qty - 1)} className="p-1.5 border border-zinc-200 rounded hover:bg-zinc-50"><Minus size={12} weight="bold"/></button>
                    <input type="number" value={it.qty} onChange={(e)=>setQty(it.id, parseInt(e.target.value)||0)} className="w-14 text-center border border-zinc-200 rounded numeric h-7 text-sm" />
                    <button onClick={()=>setQty(it.id, Math.min(it.stock, it.qty + 1))} className="p-1.5 border border-zinc-200 rounded hover:bg-zinc-50" disabled={it.qty >= it.stock}><Plus size={12} weight="bold"/></button>
                  </div>
                  <div className="text-right w-24">
                    <div className="numeric font-bold text-sm">{fmtCur(it.portal_price * it.qty, it.currency)}</div>
                    <button onClick={()=>setQty(it.id, 0)} className="text-[10px] text-zinc-400 hover:text-[#E0000F] inline-flex items-center gap-0.5 mt-0.5"><Trash size={10}/> Quitar</button>
                  </div>
                </div>
              ))}
              <div className="border-t border-zinc-200 pt-4 mt-4 flex justify-between items-center">
                <span className="font-heading text-lg">Total estimado</span>
                <span className="numeric text-2xl font-bold text-[#002FA7]">{fmtCur(cartTotal, cartCurrency)}</span>
              </div>
              <p className="text-[11px] text-zinc-500 bg-zinc-50 p-3 rounded">
                Esta es una <b>solicitud de compra</b>. Al enviarla, el equipo de Alendra PC te contactará para confirmar disponibilidad y coordinar el pago. No se hace ningún cobro automático.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setShowCart(false)}>Seguir comprando</Button>
            <Button onClick={submitOrder} disabled={cartItems.length === 0 || submitting} className="bg-[#00873D] hover:bg-[#006e30] text-white" data-testid="submit-order-btn">
              {submitting ? "Enviando…" : "Enviar solicitud"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MY ORDERS */}
      <Dialog open={showOrders} onOpenChange={setShowOrders}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Mis solicitudes</DialogTitle>
          </DialogHeader>
          {orders.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 dotted-bg">Aún no tienes solicitudes.</div>
          ) : (
            <div className="space-y-3">
              {orders.map(o => {
                const STATUS_LABEL = { pending: "Pendiente", contacted: "En contacto", converted: "Convertida a nota", rejected: "Rechazada" };
                const STATUS_COLOR = { pending: "#FFC000", contacted: "#1E88E5", converted: "#00873D", rejected: "#E0000F" };
                return (
                  <div key={o.id} className="border border-zinc-200 p-4 rounded-sm">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="text-xs font-mono-data text-zinc-400">{new Date(o.created_at).toLocaleString("es-MX")}</div>
                        <div className="numeric text-lg font-bold">{fmtCur(o.total, o.currency)}</div>
                      </div>
                      <span className="text-[11px] font-semibold px-2 py-1 rounded" style={{ background: STATUS_COLOR[o.status]+"20", color: STATUS_COLOR[o.status] }}>
                        {STATUS_LABEL[o.status]}
                      </span>
                    </div>
                    <div className="text-xs text-zinc-600 space-y-0.5">
                      {o.items.map((it, i) => <div key={`${it.product_id || "i"}-${i}`}>· {it.qty}× {it.product_name} — {fmtCur(it.subtotal, it.currency)}</div>)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CHANGE PWD */}
      <Dialog open={showChangePwd} onOpenChange={setShowChangePwd}>
        <DialogContent>
          <DialogHeader><DialogTitle>{me.must_change_password ? "Cambia tu contraseña temporal" : "Cambiar contraseña"}</DialogTitle></DialogHeader>
          {me.must_change_password && (
            <p className="text-[11px] text-zinc-500 -mt-2">Recomendamos cambiarla ahora, pero puedes cerrar esta ventana y hacerlo después con el botón <Lock size={10} className="inline -mt-0.5"/></p>
          )}
          <form onSubmit={changePassword} className="space-y-3">
            <div>
              <Label className="text-xs uppercase">Contraseña actual</Label>
              <PasswordInput value={pwd.cur} onChange={(e)=>setPwd({...pwd, cur: e.target.value})} required className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase">Nueva contraseña</Label>
              <PasswordInput value={pwd.next} onChange={(e)=>setPwd({...pwd, next: e.target.value})} required minLength={10} className="mt-1" />
              <div className="text-[11px] text-zinc-500 mt-1">{PASSWORD_HINT}</div>
            </div>
            <div>
              <Label className="text-xs uppercase">Confirmar</Label>
              <PasswordInput value={pwd.confirm} onChange={(e)=>setPwd({...pwd, confirm: e.target.value})} required className="mt-1" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setShowChangePwd(false)}>{me.must_change_password ? "Después" : "Cancelar"}</Button>
              <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] text-white">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PhotoLightbox
        open={lb.open}
        onClose={() => setLb((s) => ({ ...s, open: false }))}
        photos={lb.photos}
        initialIndex={lb.initialIndex}
        title={lb.title}
        filenameBase={lb.title}
      />
    </div>
  );
}
